/** Settle a cancelled read even when an SDK promise cannot itself be cancelled. */
export function throwIfReadAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('The read was cancelled.', 'AbortError');
  }
}

export function awaitAbortableRead<T>(read: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return read;
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(signal.reason ?? new DOMException('The read was cancelled.', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    // Attach both handlers even after cancellation: a late SDK rejection must
    // not become unhandled, and a late success cannot resume the caller.
    read.then(
      value => {
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted) onAbort();
        else resolve(value);
      },
      error => {
        signal.removeEventListener('abort', onAbort);
        reject(signal.aborted ? signal.reason ?? error : error);
      },
    );
    if (signal.aborted) onAbort();
  });
}
